import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useAuth } from '../hooks/useAuth';
import { useIaJobs } from './IaJobsProvider';
import { workoutApi } from '../services/api';
import {
  readOnboardingDraft,
  writeOnboardingDraft,
  clearOnboardingDraft,
  purgeLegacyOnboardingDrafts,
} from '../utils/onboardingDraftStorage';
import { DEFAULT_COMPOSITION, normalizeComposition, hydrateCompositionFromDraft } from '../constants/workoutCompositionSegments';
import { readAndClearSignupCompositionBridge } from '../utils/signupProfileCache';

const OnboardingPackContext = createContext(null);

function parseDesiredCount(value) {
  const n = parseInt(value, 10);
  return Number.isFinite(n) && n >= 2 && n <= 4 ? n : 3;
}

export function OnboardingPackProvider({ children }) {
  const { user } = useAuth();
  const { setOnboardingReview, clearOnboardingReview, openOnboardingReview } = useIaJobs();
  const userId = user?.id;

  const [suggestions, setSuggestions] = useState([]);
  const [perWorkoutNotes, setPerWorkoutNotes] = useState([]);
  const [desiredCount, setDesiredCount] = useState('3');
  const [globalNotes, setGlobalNotes] = useState('');
  const [extraDetail, setExtraDetail] = useState('');
  const [workoutComposition, setWorkoutComposition] = useState([...DEFAULT_COMPOSITION]);
  const [compositionExplicit, setCompositionExplicit] = useState(false);
  const [engineLabel, setEngineLabel] = useState('IA');
  const [draftSavedAt, setDraftSavedAt] = useState(null);
  const [hydrated, setHydrated] = useState(false);

  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardMode, setWizardMode] = useState('voluntary');
  const [wizardInitialStep, setWizardInitialStep] = useState('profile');
  const [wizardContextLabel, setWizardContextLabel] = useState('plan_wizard');
  const [regenerationRound, setRegenerationRound] = useState(0);

  const syncReview = useCallback(
    (count) => {
      if (count > 0) setOnboardingReview({ count });
      else clearOnboardingReview();
    },
    [setOnboardingReview, clearOnboardingReview]
  );

  const clearDraft = useCallback(() => {
    if (userId) clearOnboardingDraft(userId);
    setSuggestions([]);
    setPerWorkoutNotes([]);
    setDraftSavedAt(null);
    clearOnboardingReview();
  }, [userId, clearOnboardingReview]);

  useEffect(() => {
    if (!userId) {
      setSuggestions([]);
      setPerWorkoutNotes([]);
      setHydrated(false);
      return;
    }

    purgeLegacyOnboardingDrafts(userId);
    const d = readOnboardingDraft(userId);
    const draftSuggestions = Array.isArray(d?.suggestions) ? d.suggestions : [];

    if (draftSuggestions.length > 0) {
      setSuggestions(draftSuggestions);
      setPerWorkoutNotes(
        Array.isArray(d.perWorkoutNotes) && d.perWorkoutNotes.length === draftSuggestions.length
          ? d.perWorkoutNotes
          : Array(draftSuggestions.length).fill('')
      );
      syncReview(draftSuggestions.length);
      setDraftSavedAt(typeof d?.savedAt === 'string' ? d.savedAt : null);
    } else {
      setSuggestions([]);
      setPerWorkoutNotes([]);
      setDraftSavedAt(null);
    }

    if (typeof d?.desiredCount === 'number' && d.desiredCount >= 2 && d.desiredCount <= 4) {
      setDesiredCount(String(d.desiredCount));
    }
    if (typeof d?.globalNotes === 'string') setGlobalNotes(d.globalNotes);
    if (typeof d?.extraDetail === 'string') setExtraDetail(d.extraDetail);
    if (Array.isArray(d?.workoutComposition) && d.workoutComposition.length > 0) {
      setWorkoutComposition(hydrateCompositionFromDraft(d.workoutComposition));
    } else {
      const bridged = readAndClearSignupCompositionBridge();
      if (bridged) {
        setWorkoutComposition(hydrateCompositionFromDraft(bridged));
        setCompositionExplicit(true);
      }
    }

    setHydrated(true);
  }, [userId, syncReview]);

  useEffect(() => {
    if (!userId || !hydrated || suggestions.length === 0) return;
    writeOnboardingDraft(userId, {
      suggestions,
      desiredCount: parseDesiredCount(desiredCount),
      globalNotes,
      extraDetail,
      perWorkoutNotes,
      workoutComposition,
    });
  }, [
    suggestions,
    perWorkoutNotes,
    desiredCount,
    globalNotes,
    extraDetail,
    workoutComposition,
    userId,
    hydrated,
  ]);

  const setJobResult = useCallback(
    (arr, { engine, openReview = false } = {}) => {
      const list = Array.isArray(arr) ? arr : [];
      if (list.length === 0) {
        if (userId) clearOnboardingDraft(userId);
        setSuggestions([]);
        setPerWorkoutNotes([]);
        clearOnboardingReview();
        return;
      }
      const nextNotes = Array(list.length).fill('');
      setSuggestions(list);
      setPerWorkoutNotes(nextNotes);
      if (engine) setEngineLabel(engine);
      setDraftSavedAt(new Date().toISOString());
      syncReview(list.length);
      if (openReview) openOnboardingReview();
    },
    [userId, syncReview, clearOnboardingReview, openOnboardingReview]
  );

  const prepareNewGeneration = useCallback(() => {
    clearOnboardingReview();
    setSuggestions([]);
    setPerWorkoutNotes([]);
    setGlobalNotes('');
    setExtraDetail('');
    setDesiredCount('3');
    setCompositionExplicit(false);
    setWorkoutComposition([...DEFAULT_COMPOSITION]);
    if (userId) {
      clearOnboardingDraft(userId);
    }
  }, [userId, clearOnboardingReview]);

  const removeSuggestion = useCallback(
    (idx) => {
      const nextS = suggestions.filter((_, i) => i !== idx);
      const nextN = perWorkoutNotes.filter((_, i) => i !== idx);
      setSuggestions(nextS);
      setPerWorkoutNotes(nextN);
      if (nextS.length === 0) {
        clearDraft();
      } else {
        syncReview(nextS.length);
      }
    },
    [suggestions, perWorkoutNotes, clearDraft, syncReview]
  );

  const updatePerWorkoutNote = useCallback((idx, value) => {
    setPerWorkoutNotes((prev) => {
      const next = [...prev];
      next[idx] = value;
      return next;
    });
  }, []);

  const applyOne = useCallback(
    async (suggestion, idx) => {
      await workoutApi.applySuggestion({ suggestion, replaceExisting: false });
      const nextS = suggestions.filter((_, i) => i !== idx);
      const nextN = perWorkoutNotes.filter((_, i) => i !== idx);
      setSuggestions(nextS);
      setPerWorkoutNotes(nextN);
      if (nextS.length === 0) {
        if (userId) clearOnboardingDraft(userId);
        clearOnboardingReview();
      } else {
        syncReview(nextS.length);
      }
      return { message: `Treino "${suggestion.name}" criado.` };
    },
    [suggestions, perWorkoutNotes, userId, clearOnboardingReview, syncReview]
  );

  const applyAll = useCallback(async () => {
    const created = [];
    const createdIds = [];
    try {
      for (let i = 0; i < suggestions.length; i += 1) {
        const sug = suggestions[i];
        const res = await workoutApi.applySuggestion({ suggestion: sug, replaceExisting: false });
        const workout = res?.data;
        created.push(sug.name);
        if (workout?.id != null) createdIds.push(workout.id);
      }
      clearDraft();
      const message =
        created.length === 1
          ? `Treino "${created[0]}" criado.`
          : `${created.length} treinos criados.`;
      return { message, createdIds, count: created.length };
    } catch (e) {
      const appliedCount = created.length;
      const nextS = suggestions.slice(appliedCount);
      const nextN = perWorkoutNotes.slice(appliedCount);
      setSuggestions(nextS);
      setPerWorkoutNotes(nextN);
      if (nextS.length === 0) {
        if (userId) clearOnboardingDraft(userId);
        clearOnboardingReview();
      } else {
        syncReview(nextS.length);
      }
      const partial =
        created.length > 0 ? ` Criados antes do erro: ${created.join(', ')}.` : '';
      const errMsg =
        (e.response?.data?.error || e.message || 'Erro ao criar treinos') + partial;
      throw new Error(errMsg);
    }
  }, [suggestions, perWorkoutNotes, clearDraft, userId, clearOnboardingReview, syncReview]);

  const openWizard = useCallback(
    ({ mode = 'voluntary', contextLabel = 'plan_wizard', step = 'profile' } = {}) => {
      setWizardMode(mode);
      setWizardContextLabel(contextLabel);
      setWizardInitialStep(step);
      setWizardOpen(true);
    },
    []
  );

  const closeWizard = useCallback(() => {
    setWizardOpen(false);
  }, []);

  const incrementRegenerationRound = useCallback(() => {
    setRegenerationRound((r) => r + 1);
  }, []);

  const resetRegenerationRound = useCallback(() => {
    setRegenerationRound(0);
  }, []);

  const value = useMemo(
    () => ({
      suggestions,
      perWorkoutNotes,
      desiredCount,
      setDesiredCount,
      globalNotes,
      setGlobalNotes,
      extraDetail,
      setExtraDetail,
      workoutComposition,
      setWorkoutComposition: (next, meta) => {
        setWorkoutComposition(next);
        if (meta?.explicit === true) setCompositionExplicit(true);
        else if (meta?.explicit === false) setCompositionExplicit(false);
      },
      compositionExplicit,
      engineLabel,
      setEngineLabel,
      draftSavedAt,
      setJobResult,
      prepareNewGeneration,
      removeSuggestion,
      updatePerWorkoutNote,
      clearDraft,
      applyOne,
      applyAll,
      wizardOpen,
      wizardMode,
      wizardInitialStep,
      wizardContextLabel,
      openWizard,
      closeWizard,
      regenerationRound,
      incrementRegenerationRound,
      resetRegenerationRound,
    }),
    [
      suggestions,
      perWorkoutNotes,
      desiredCount,
      globalNotes,
      extraDetail,
      workoutComposition,
      compositionExplicit,
      engineLabel,
      draftSavedAt,
      setJobResult,
      prepareNewGeneration,
      removeSuggestion,
      updatePerWorkoutNote,
      clearDraft,
      applyOne,
      applyAll,
      wizardOpen,
      wizardMode,
      wizardInitialStep,
      wizardContextLabel,
      openWizard,
      closeWizard,
      regenerationRound,
      incrementRegenerationRound,
      resetRegenerationRound,
    ]
  );

  return (
    <OnboardingPackContext.Provider value={value}>{children}</OnboardingPackContext.Provider>
  );
}

export function useOnboardingPack() {
  const ctx = useContext(OnboardingPackContext);
  if (!ctx) {
    throw new Error('useOnboardingPack must be used within OnboardingPackProvider');
  }
  return ctx;
}
